import { useQueryClient } from "@tanstack/react-query";
import { format, toZonedTime } from "date-fns-tz";
import { useFlags } from "flagsmith/react";
import { Formik, setIn } from "formik";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchAndFilterTypes } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import useUser from "src/hooks/useUser";
import { triggerDownloadFromUrl } from "src/services/dataTransfer";
import { OrgAdmin } from "src/services/url";
import { Status } from "src/types/authData";
import { BulkExportFormProps } from ".";
import {
  ExportRequest,
  GoogleDriveAuthMethodType,
  GoogleDriveConfigSchema,
  GoogleDriveConfigType,
  ScheduleRequest,
  ScheduleResponse,
} from "../../dataServices/exports/types";
import { useExportManagementAPI } from "../../dataServices/exports/useExportManagementAPI";
import { DataServiceTab } from "../../dataServices/useCreateDataService";
import {
  getAllColumnGroups,
  getAllColumnInitialValues,
  getExportColumnKeysFromSearchParams,
  isQuestionColumnKey,
} from "./columnUtils";
import { ConfigureExportPage } from "./ConfigureExportPage";
import {
  getNextRunTime,
  isFutureExportTime,
  isTimeZoneUtc,
  REPEAT_MODE_OPTIONS,
} from "./dateUtils";
import { SelectColumnsPage } from "./SelectColumnsPage";
import {
  BulkExportFormType,
  ExportFormPage,
  ExportTimeMode,
  getSelectedTime,
} from "./types";

export const BulkExportForm: React.FC<BulkExportFormProps> = ({
  formTemplateId,
  selection,
  questionIdsAndLabelsInOrder,
  formVerifications,
  tagGroups,
  formTemplateConfig,
  dropoffFormTemplateId,
  scheduledExport,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useUser();
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const api = useExportManagementAPI();
  const flags = useFlags([
    "current-applying-schools",
    "saved-view-custom-columns",
  ]);
  const isCurrentApplyingSchoolsEnabled =
    flags["current-applying-schools"].enabled;
  const isSavedViewCustomColumnsEnabled =
    flags["saved-view-custom-columns"].enabled;

  const organization = useOrganization();
  const timezoneName = organization?.hasData()
    ? organization.data.timezone_name
    : "";
  const orgPath = organization?.hasData() ? organization.data.path : "";
  const { isSchoolAdmin } = useSchoolAdmin();

  const [currentPage, setCurrentPage] = useState(ExportFormPage.SelectColumns);
  const [disableEditSFTP, setDisableEditSFTP] = useState<boolean>(
    !!scheduledExport && !!scheduledExport.sftpConfig
  );

  const columnGroups = getAllColumnGroups({
    ...formTemplateConfig,
    isRankingEnabled: !isSchoolAdmin && formTemplateConfig.isRankingEnabled,
    orgPath,
    isCurrentApplyingSchoolsEnabled,
    questionIdsAndLabelsInOrder,
    formVerifications,
    tagGroups,
  });

  const allColumns = getAllColumnInitialValues(
    columnGroups,
    formTemplateConfig.isLotteryEnabled
  );
  const selectedColumnsFromSearchParams = getExportColumnKeysFromSearchParams(
    searchParams,
    formVerifications?.map((verification) => verification.id)
  );
  const selectedColumns =
    isSavedViewCustomColumnsEnabled && selectedColumnsFromSearchParams
      ? selectedColumnsFromSearchParams
          .map((key) => ({
            [key]: true,
          }))
          .reduce((acc, curr) => ({ ...acc, ...curr }), {})
      : allColumns;

  const initialValues = useMemo(
    () => getInitialValues(selectedColumns, allColumns, scheduledExport),
    [selectedColumns, allColumns, scheduledExport]
  );

  const errorHandler = (error: unknown, toastId: string) => {
    toast.error({
      title: "Error exporting forms",
      duration: null,
      id: toastId,
    });
    console.error(JSON.stringify(error));
  };

  // TODO: replace with Zod schema validation
  const validate = (values: BulkExportFormType) => {
    let errors: { [key: string]: string } = {};

    // --- SFTP ---
    if (values.sftp.enabled && !disableEditSFTP) {
      const cfg = values.sftp.config;
      for (const key of [
        "host",
        "port",
        "path",
        "username",
        "authMethod",
        "secret",
      ] as const) {
        if (!cfg?.[key]) {
          errors = setIn(
            errors,
            `sftp.config.${key}`,
            `SFTP ${key} is required`
          );
        }
      }
    }

    // --- Google Drive ---
    if (values.googleDrive.enabled) {
      const cfg = values.googleDrive.config;
      if (!cfg?.type) {
        errors = setIn(
          errors,
          "googleDrive.config.type",
          "Google drive config is required"
        );
      } else {
        switch (cfg.type) {
          case GoogleDriveConfigType.UpdateSheet:
            if (!cfg.spreadsheetId) {
              errors = setIn(
                errors,
                "googleDrive.config.spreadsheetId",
                "Spreadsheet ID is required"
              );
            }
            break;

          case GoogleDriveConfigType.CreateSheet:
          case GoogleDriveConfigType.UploadCSV:
            if (!cfg.folderId) {
              errors = setIn(
                errors,
                "googleDrive.config.folderId",
                "Folder ID is required"
              );
            }
            break;
        }
      }
    }

    // --- Time ---
    if (values.exportTime.mode === ExportTimeMode.Later) {
      if (!values.exportTime.presetTime) {
        errors = setIn(errors, "exportTime.presetTime", "Time is required");
      }
      if (values.exportTime.presetTime === "custom") {
        if (!values.exportTime.customTime) {
          errors = setIn(errors, "exportTime.customTime", "Time is required");
        } else if (!isFutureExportTime(values.exportTime.customTime)) {
          errors = setIn(
            errors,
            "exportTime.customTime",
            "Time must be in the future"
          );
        }
      }
      if (
        values.exportTime.repeat?.enabled &&
        !values.exportTime.repeat?.mode
      ) {
        errors = setIn(
          errors,
          "exportTime.repeat.mode",
          "Repeat mode is required when repeat is enabled"
        );
      }
    }

    return errors;
  };

  const onSubmitHandler = async (values: BulkExportFormType) => {
    const exportSearchParams = new URLSearchParams(
      scheduledExport
        ? scheduledExport.exportConfig.searchParamsString
        : searchParams
    );
    if (dropoffFormTemplateId) {
      // Replace dropoffFormTemplateId in searchParams with the value of the prop dropoffFormTemplateId,
      // which is needed for the search query in the backend.
      // (The existing value is the same as formTemplate.id.)
      exportSearchParams.set(
        SearchAndFilterTypes.DropoffFormTemplateId,
        dropoffFormTemplateId
      );
    }
    // Remove visible columns as it's saved separately in selectedColumns
    exportSearchParams.delete(SearchAndFilterTypes.VisibleColumns);

    const searchParamsString = exportSearchParams.toString();

    const organizationId = organization.toNullable()?.id;
    if (user.status !== Status.OK && user.status !== Status.NOT_VERIFIED) {
      throw new Error("Can't find current user");
    }
    const login = user.data.loginType;
    if (login.type !== "email") {
      throw new Error("User does not have email");
    }

    const email = login.email;

    const questionIds = questionIdsAndLabelsInOrder.map(
      (question) => question.id
    );
    const selectedQuestionIds = questionIds.filter(
      (id) => values.selectedColumns[`question-${id}`]
    );
    const selectedColumns = Object.keys(values.selectedColumns)
      .filter((key) => !isQuestionColumnKey(key))
      .filter((key) => values.selectedColumns[key]);

    const postData: ExportRequest = {
      exportConfig: {
        searchParamsString,
        selection,
        formTemplateId,
        selectedQuestionIds,
        selectedColumns,
        questionKeysAsHeaders: values.options.questionKeysAsHeaders,
        exportSelectAnswerValues: values.options.exportSelectAnswerValues,
      },
      exportName: values.exportName,
      email,
      ...(!values.sftp.enabled
        ? { sftpConfig: null } // Explicitly set to null if sftp is not enabled
        : disableEditSFTP
        ? {} // Leave empty to preserve the existing sftpConfig if edit sftp is disabled
        : { sftpConfig: values.sftp.config }),
      ...(values.googleDrive.enabled
        ? {
            googleSheetsConfig: GoogleDriveConfigSchema.parse({
              ...values.googleDrive.config,
              authMethod: GoogleDriveAuthMethodType.ServiceAccount,
            }),
          }
        : {}),
    };

    const toastId = `bulk-export-${Date.now()}`;

    try {
      let exportSubmittedText: string = "";
      if (values.exportTime.mode === ExportTimeMode.Now) {
        const response = await api.createExport(organizationId ?? "", postData);
        if (!response?.pk) {
          throw new Error("Bulk export management API error");
        }
        const { pk } = response;
        toast({
          description:
            "You'll receive an email once ready, you can close this window or navigate elsewhere if needed.",
          duration: null,
          id: toastId,
          status: "loading",
          title: "Export in progress",
          isClosable: true,
        });

        // Poll for completion and auto-download
        const checkExportCompletion = async () => {
          try {
            const response = await api.getOneExport(organizationId ?? "", pk);
            if (!response || response.status === "FAILED") {
              throw new Error("Export failed");
            }
            if (response.status === "COMPLETED") {
              if (!response.url) {
                throw new Error("Export failed");
              }
              clearInterval(pollingInterval);
              triggerDownloadFromUrl(response.url);
              toast.update(toastId, {
                description:
                  "It has been automatically downloaded and sent to your email.",
                status: "success",
                title: "Export ready",
                isClosable: true,
              });
            }
          } catch (error) {
            clearInterval(pollingInterval);
            errorHandler(error, toastId);
          }
        };
        const pollingInterval = setInterval(
          async () => await checkExportCompletion(),
          5000
        );
        exportSubmittedText =
          "You'll receive an email once your export is ready.";
      }
      if (values.exportTime.mode === ExportTimeMode.Later) {
        const startTime = getSelectedTime(values);
        const repeatMode = values.exportTime.repeat?.enabled
          ? values.exportTime.repeat.mode
          : null;
        const adjustForDST = values.exportTime.repeat?.enabled
          ? values.exportTime.adjustForDST
          : null;
        if (!startTime || repeatMode === undefined || !organizationId) {
          throw new Error("Form is missing required values");
        }
        const schedulePostData: ScheduleRequest = {
          ...postData,
          scheduleConfig: {
            startTime: startTime.toISOString(),
            repeatMode,
            timeZone: adjustForDST ? timezoneName : undefined,
          },
        };
        if (scheduledExport) {
          const response = await api.editSchedule(
            organizationId,
            scheduledExport.pk,
            schedulePostData
          );
          if (response?.pk === scheduledExport.pk) {
            await queryClient.invalidateQueries({
              queryKey: [
                "getScheduledExport",
                organizationId,
                scheduledExport.pk,
              ],
            });
          }
        } else {
          await api.scheduleExport(organizationId, schedulePostData);
        }
        await queryClient.invalidateQueries({
          queryKey: ["getScheduledExports", organizationId],
        });
        const zonedTime = toZonedTime(startTime, timezoneName);
        exportSubmittedText = `You'll receive an email once your export is ready, which will run ${
          repeatMode
            ? REPEAT_MODE_OPTIONS[repeatMode].formatDescription(
                zonedTime,
                timezoneName,
                !!values.exportTime.adjustForDST
              )
            : `on ${format(zonedTime, "MMMM d, yyyy 'at' h:mmaaa zzz", {
                timeZone: timezoneName,
              })}`
        }.`;
      }
      if (scheduledExport) {
        navigate(
          organization
            .map(
              (org) =>
                `${OrgAdmin.DataServices.index(org)}?tab=${
                  DataServiceTab.ScheduledExports
                }`
            )
            .withDefault("#"),
          {
            state: {
              scheduledExportUpdated: true,
            },
          }
        );
      } else {
        navigate(
          organization
            .map((org) =>
              OrgAdmin.Forms.index({
                organization: org,
                formTemplateId,
                params: searchParams.toString(),
              })
            )
            .withDefault("#"),
          {
            state: {
              exportSubmittedText,
              scheduled: values.exportTime.mode === ExportTimeMode.Later,
            },
          }
        );
      }
    } catch (error: any) {
      errorHandler(error, toastId);
    }
  };

  return (
    <Formik<BulkExportFormType>
      initialValues={initialValues}
      onSubmit={onSubmitHandler}
      validate={validate}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {(props) => {
        return currentPage === ExportFormPage.SelectColumns ? (
          <SelectColumnsPage
            {...props}
            setCurrentPage={setCurrentPage}
            columnGroups={columnGroups}
          />
        ) : (
          <ConfigureExportPage
            {...props}
            setCurrentPage={setCurrentPage}
            isEditingScheduledExport={!!scheduledExport}
            disableEditSFTP={disableEditSFTP}
            setDisableEditSFTP={setDisableEditSFTP}
          />
        );
      }}
    </Formik>
  );
};

function getInitialValues(
  selectedColumns: { [key: string]: boolean },
  allColumns: { [key: string]: boolean },
  scheduledExport?: ScheduleResponse
): BulkExportFormType {
  const scheduledExportConfig = scheduledExport?.exportConfig;
  const selectedColumnsFromScheduledExport = scheduledExportConfig
    ? [
        ...(scheduledExportConfig.selectedColumns ?? []),
        ...(scheduledExportConfig.selectedQuestionIds?.map(
          (id) => `question-${id}`
        ) ?? []),
      ]
    : [];
  return scheduledExport
    ? {
        // Legacy scheduled export may not have selected columns set
        selectedColumns:
          selectedColumnsFromScheduledExport.length > 0
            ? selectedColumnsFromScheduledExport
                .map((key) => ({
                  [key]: true,
                }))
                .reduce((acc, curr) => ({ ...acc, ...curr }), {})
            : allColumns,
        exportName: scheduledExport.exportName ?? "",
        options: {
          questionKeysAsHeaders:
            scheduledExport.exportConfig.questionKeysAsHeaders,
          exportSelectAnswerValues:
            scheduledExport.exportConfig.exportSelectAnswerValues,
        },
        emailEnabled: true,
        sftp: {
          enabled: !!scheduledExport.sftpConfig,
          ...(scheduledExport.sftpConfig
            ? { config: { ...scheduledExport?.sftpConfig, secret: "" } }
            : {}),
        },
        googleDrive: {
          enabled: !!scheduledExport.googleSheetsConfig,
          ...(scheduledExport.googleSheetsConfig
            ? { config: scheduledExport.googleSheetsConfig }
            : {}),
        },
        exportTime: {
          mode: ExportTimeMode.Later,
          presetTime: "custom",
          customTime: getNextRunTime(
            scheduledExport.exportTime.repeatMode,
            scheduledExport.exportTime.startTime
          ).toISOString(),
          adjustForDST: !isTimeZoneUtc(scheduledExport.exportTime.timeZone),
          ...(scheduledExport.exportTime.repeatMode
            ? {
                repeat: {
                  enabled: true,
                  mode: scheduledExport.exportTime.repeatMode,
                },
              }
            : {}),
        },
      }
    : {
        selectedColumns,
        exportName: "",
        options: {
          questionKeysAsHeaders: true,
          exportSelectAnswerValues: false,
        },
        emailEnabled: true,
        sftp: {
          enabled: false,
        },
        googleDrive: {
          enabled: false,
        },
        exportTime: {
          mode: ExportTimeMode.Now,
          adjustForDST: true,
        },
      };
}
